// src/pages/Visite.jsx
// ✅ Page "Visites guidées" =
// 1) 2 boutons au début: (A) Remplir formulaire (demande) (B) Choisir une visite existante
// 2) Mode "visites existantes": cartes + bouton "Voir détails" + bouton "Réserver" (modals scrollables)
// 3) Réservation d’une visite existante => POST /api/reservations/visites/demande (mêmes champs que le formulaire)
//    + on ajoute "visite: tour._id" (si ton validator l'accepte; sinon on l'enlèvera au moment backend)

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useSpeechToText from "../hooks/useSpeechToText";
import VoiceDictation from "../components/VoiceDictation";
import useMicLevel from "../hooks/useMicLevel";
import zelijPattern from "../assets/zelij3.png";

import {
  MapPin,
  Calendar as CalendarIcon,
  Users,
  Tag,
  BadgePercent,
  Send,
  User,
  Mail,
  Phone,
  Home,
  MessageSquare,
  Wallet,
  Clock,
  Star,
  X,
  Eye,
  CheckCircle2,
  Search,
  Filter,
} from "lucide-react";

import toast from "react-hot-toast";
import { notifyApiError } from "../utils/apiError";
import cmiService from "../services/cmiService";
import { getPaymentMode } from "../services/settingsService";
import { useAuth } from "../contexts/AuthContext";
import { t as tr } from "../contexts/translations";

const API_BASE =
  (process.env.REACT_APP_API_BASE_URL || process.env.VITE_API_BASE_URL || "https://nouzhaback-theta.vercel.app").replace(
    /\/$/,
    ""
  );

/* =========================
   Cities Picker
========================= */
const moroccanCities = [
  "Casablanca",
  "Rabat",
  "Fès",
  "Marrakech",
  "Merzouga",
  "Agadir",
  "Tanger",
  "Meknès",
  "Oujda",
  "Kenitra",
  "Tétouan",
  "Safi",
  "Mohammedia",
  "Khouribga",
  "El Jadida",
  "Béni Mellal",
  "Errachidia",
  "Taza",
  "Essaouira",
  "Ksar el-Kebir",
  "Settat",
  "Larache",
  "Khemisset",
  "Guelmim",
  "Berrechid",
  "Wazzane",
  "Ouarzazate",
  "Tiznit",
  "Taourirt",
  "Nador",
  "Berkane",
  "Sidi Kacem",
  "Khénifra",
  "Sidi Slimane",
  "Al Hoceima",
  "Taroudant",
  "Oulad Teima",
  "Youssoufia",
  "Fquih Ben Salah",
  "Ouezzane",
  "Guercif",
  "Ifrane",
  "Azrou",
  "Midelt",
  "Zagora",
  "Tinghir",
  "Chefchaouen",
  "Autre",
].sort();

/* =========================
   Utils
========================= */
const getToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split("T")[0];
};

const formatDate = (isoOrDate, locale = "fr") => {
  try {
    const d = new Date(isoOrDate);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(locale === "ar" ? "ar-MA" : locale === "en" ? "en-US" : "fr-FR", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return "";
  }
};

const mapTourTypeToFront = (type) => {
  // DB: ['culturel','historique','nature','gastronomique','autre']
  // Front (demande): cultural/historical/nature/adventure...
  const v = String(type || "").toLowerCase();
  if (v === "culturel") return "cultural";
  if (v === "historique") return "historical";
  if (v === "nature") return "nature";
  // gastronomie => "cultural" par défaut (tu peux changer)
  if (v === "gastronomique") return "cultural";
  return "";
};

const normalizeList = (arr) => (Array.isArray(arr) ? arr.filter(Boolean).map(String) : []);

/* =========================
   Modal wrapper (scrollable)
========================= */
const ModalShell = ({ open, onClose, title, children }) => {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center px-3 md:px-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* backdrop */}
          <div className="absolute inset-0 bg-black/45" onClick={onClose} />

          {/* panel */}
          <motion.div
            initial={{ y: 18, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 18, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl border border-orange-100 overflow-hidden"
          >
            <div className="flex items-center justify-between gap-3 px-5 md:px-6 py-4 border-b bg-white">
              <div className="min-w-0">
                <div className="text-lg md:text-xl font-bold text-gray-900 truncate">{title}</div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl hover:bg-gray-100 transition"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            {/* ✅ scroll area */}
            <div className="max-h-[75vh] overflow-y-auto px-5 md:px-6 py-5">{children}</div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

const GuidedTours = ({ language }) => {
  const { currentUser } = useAuth();
  const today = useMemo(() => getToday(), []);

  // ✅ helper translation with fallback
  const T = (key, fallback) => {
    const v = tr(key, language);
    return v === key ? fallback : v;
  };

  // ---------- Mode switch ----------
  const [mode, setMode] = useState("form"); // "form" | "existing"

  // ---------- Speech-to-Text (Web Speech API) ----------
  const stt = useSpeechToText({
    lang: language === "ar" ? "ar-MA" : language === "en" ? "en-US" : "fr-FR",
    continuous: true,
    interimResults: true,
  });
  useMicLevel(stt.listening);

  // ---------- Form states (DEMANDE) ----------
  const [selectedCity, setSelectedCity] = useState("");
  const [customLocation, setCustomLocation] = useState("");

  const [date, setDate] = useState("");
  const [tourType, setTourType] = useState("");
  const [numberOfPeople, setNumberOfPeople] = useState(1);

  // Pré-remplissage depuis la date choisie dans le hero (?date).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const d = searchParams.get("date");
    if (d) setDate(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [dureeMin, setDureeMin] = useState(2);
  const [dureeMax, setDureeMax] = useState(8);

  const [prixMin, setPrixMin] = useState(150);
  const [prixMax, setPrixMax] = useState(1500);

  const [souhaiteReduction, setSouhaiteReduction] = useState(false);
  const [comments, setComments] = useState("");

  // ---------- Personal info ----------
  const [nomComplet, setNomComplet] = useState("");
  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [adresse, setAdresse] = useState("");
  const [acceptCgv, setAcceptCgv] = useState(false);
  const [formStep, setFormStep] = useState(0);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // ---------- Existing tours ----------
  const [tours, setTours] = useState([]);
  const [toursLoading, setToursLoading] = useState(false);
  const [toursError, setToursError] = useState("");

  const [q, setQ] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // ---------- Modals ----------
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [activeTour, setActiveTour] = useState(null);

  // ---------- Reservation modal fields (for existing tour) ----------
  const [rDate, setRDate] = useState("");
  const [rPeople, setRPeople] = useState(1);
  const [rComments, setRComments] = useState("");
  const [rNom, setRNom] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rTel, setRTel] = useState("");
  const [rAddr, setRAddr] = useState("");
  const [rAccept, setRAccept] = useState(false);
  const [rSending, setRSending] = useState(false);
  const [paymentMode, setPaymentMode] = useState("email"); // "direct" (CMI) ou "email" (demande)

  useEffect(() => {
    getPaymentMode("visite").then(setPaymentMode).catch(() => {});
  }, []);

  // keep email in sync when logged
  useEffect(() => {
    setEmail(currentUser?.email || "");
    setREmail(currentUser?.email || "");
  }, [currentUser]);

  const finalLocation = useMemo(() => {
    if (selectedCity === "Autre") return (customLocation || "").trim();
    return (selectedCity || "").trim();
  }, [selectedCity, customLocation]);

  // ======== Fetch tours (existing list) ========
  const fetchTours = async () => {
    try {
      setToursError("");
      setToursLoading(true);

      const res = await fetch(`${API_BASE}/api/visites/`);
      const data = await res.json();

      // support shapes: {success,data} OR array
      const list = Array.isArray(data) ? data : data?.data || data?.visites || [];
      setTours(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setToursError(T("toursLoadError", "Impossible de charger les visites."));
    } finally {
      setToursLoading(false);
    }
  };

  useEffect(() => {
    if (mode === "existing") fetchTours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const filteredTours = useMemo(() => {
    const query = q.trim().toLowerCase();

    return (tours || []).filter((t) => {
      const titre = String(t?.titre || "").toLowerCase();
      const ville = String(t?.ville || "").toLowerCase();
      // const type = String(t?.type || "").toLowerCase();
      const desc = String(t?.description || "").toLowerCase();

      const okQ = !query || titre.includes(query) || ville.includes(query) || desc.includes(query);
      const okCity = !cityFilter || String(t?.ville || "") === cityFilter;
      const okType = !typeFilter || String(t?.type || "") === typeFilter;

      return okQ && okCity && okType;
    });
  }, [tours, q, cityFilter, typeFilter]);

  // ======== Gemini autofill (same as your GuidedTours) ========
  const applyGuidedTourFields = (fields) => {
    if (!fields) return;

    if (fields.location) {
      const loc = String(fields.location).trim();
      if (moroccanCities.includes(loc)) {
        setSelectedCity(loc);
        setCustomLocation("");
      } else {
        setSelectedCity("Autre");
        setCustomLocation(loc);
      }
    }

    if (fields.date) setDate(fields.date);
    if (fields.tourType) setTourType(fields.tourType);

    if (fields.numberOfPeople != null) setNumberOfPeople(String(fields.numberOfPeople));

    if (fields.dureeHeures?.min != null) setDureeMin(Number(fields.dureeHeures.min));
    if (fields.dureeHeures?.max != null) setDureeMax(Number(fields.dureeHeures.max));

    if (fields.budgetTotal?.min != null) setPrixMin(Number(fields.budgetTotal.min));
    if (fields.budgetTotal?.max != null) setPrixMax(Number(fields.budgetTotal.max));

    if (typeof fields.comments === "string" && fields.comments.trim()) setComments(fields.comments);

    if (fields.client?.nomComplet) setNomComplet(fields.client.nomComplet);
    if (fields.client?.email && !currentUser) setEmail(fields.client.email);
    if (fields.client?.telephone) setTelephone(fields.client.telephone);
    if (fields.client?.adresse) setAdresse(fields.client.adresse);
  };

  const handleGeminiAutofill = async () => {
    const spoken = (stt.transcript + (stt.interim ? " " + stt.interim : "")).trim();
    if (!spoken) return toast.error(T("speakFirst", "Parle d'abord, puis clique sur Autofill."));

    try {
      const res = await fetch(`${API_BASE}/api/ai/parse-guided-tour`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: spoken, lang: language }),
      });

      const data = await res.json();
      if (!data?.success) {
        toast.error(data?.message || T("geminiError", "Erreur Gemini"));
        return;
      }

      applyGuidedTourFields(data.fields);
      toast.success(T("geminiAutofillSuccess", "Formulaire rempli à partir de ta voix ✅"));
    } catch (e) {
      console.error(e);
      toast.error(T("geminiNetworkError", "Erreur réseau / serveur Gemini"));
    }
  };

  // ======== DEMANDE (Form mode) ========
  const validateDemande = () => {
    if (!finalLocation) {
      toast.error(T("vChooseCity", "Veuillez choisir la ville / lieu."));
      return false;
    }
    if (!date) {
      toast.error(T("vChooseDate", "Veuillez choisir la date."));
      return false;
    }

    if (dureeMin < 1 || dureeMax < 1 || dureeMin > dureeMax) {
      toast.error(T("vDurationRange", "Veuillez vérifier l’intervalle de durée (min/max)."));
      return false;
    }

    if (prixMin < 0 || prixMax < 0 || prixMin > prixMax) {
      toast.error(T("vBudgetRange", "Veuillez vérifier l’intervalle de budget (min/max)."));
      return false;
    }

    if (!nomComplet.trim()) {
      toast.error(T("vFullName", "Veuillez saisir votre nom complet."));
      return false;
    }
    if (!email.trim()) {
      toast.error(T("vEmail", "Veuillez saisir votre email."));
      return false;
    }
    if (!telephone.trim()) {
      toast.error(T("vPhone", "Veuillez saisir votre téléphone."));
      return false;
    }
    if (!adresse.trim()) {
      toast.error(T("vAddress", "Veuillez saisir votre adresse."));
      return false;
    }

    if (!acceptCgv) {
      toast.error(T("vAcceptCgv", "Veuillez accepter les conditions de paiement (CMI/CGV)."));
      return false;
    }

    if (selectedCity === "Autre" && !customLocation.trim()) {
      toast.error(T("vSpecifyOther", "Veuillez préciser le lieu (option Autre)."));
      return false;
    }

    return true;
  };

  const getAuthHeaders = () => {
  // adapte si ton app stocke le token autrement
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    currentUser?.token ||
    currentUser?.accessToken ||
    null;

  return token ? { Authorization: `Bearer ${token}` } : {};
};

const submitDemande = async (payload) => {
  const res = await fetch(`${API_BASE}/api/reservations/visites/demande`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    throw new Error(data?.message || T("sendError", "Erreur lors de l’envoi. Veuillez réessayer."));
  }
  return data;
};


  const handleSubmitForm = async (e) => {
    e.preventDefault();
    // Garde-fou wizard : avancer tant qu'on n'est pas à la dernière étape
    if (formStep !== lastFormStep) { formNext(); return; }
    if (!validateDemande()) return;

    const payload = {
  location: finalLocation,
  date,
  tourType: tourType || null,
  numberOfPeople: Number(numberOfPeople),

  dureeHeures: { min: Number(dureeMin), max: Number(dureeMax) },
  budgetTotal: { min: Number(prixMin), max: Number(prixMax) },

  souhaiteReduction,
  comments: comments?.trim() || "",

  client: {
    nomComplet: nomComplet.trim(),
    email: email.trim(),
    telephone: telephone.trim(),
    adresse: adresse.trim(),
  },

  acceptCgv,
  lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
};


    try {
      setLoading(true);
      await submitDemande(payload);

      toast.success(T("guidedSent", "Votre demande de visite guidée a été envoyée !"));
      setSuccess(true);

      // reset
      setSelectedCity("");
      setCustomLocation("");
      setDate("");
      setTourType("");
      setNumberOfPeople(1);
      setDureeMin(2);
      setDureeMax(8);
      setPrixMin(150);
      setPrixMax(1500);
      setSouhaiteReduction(false);
      setComments("");

      setNomComplet("");
      setTelephone("");
      setAdresse("");
      setAcceptCgv(false);
      if (!currentUser) setEmail("");
    } catch (err) {
      console.error(err);
      notifyApiError(err, T("sendError", "Erreur lors de l’envoi. Veuillez réessayer."));
    } finally {
      setLoading(false);
    }
  };

  // ======== Existing tours: open modals ========
  const openDetails = (tour) => {
    setActiveTour(tour);
    setDetailsOpen(true);
  };

  const openReserve = (tour) => {
    setActiveTour(tour);
    setReserveOpen(true);

    // prefill reservation modal
    setRDate(() => {
      const d = tour?.date ? new Date(tour.date) : null;
      if (!d || isNaN(d.getTime())) return "";
      // convert to yyyy-mm-dd
      const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      return iso;
    });
    setRPeople(1);
    setRComments("");
    setRNom("");
    setRTel("");
    setRAddr("");
    setRAccept(false);

    // email sync already in effect; keep current if logged
    if (!currentUser) setREmail("");
  };

  const validateReserve = () => {
    if (!activeTour?._id) {
      toast.error(T("tourMissing", "Visite non sélectionnée."));
      return false;
    }

    if (!rDate) {
      toast.error(T("vChooseDate", "Veuillez choisir la date."));
      return false;
    }

    const n = Number(rPeople || 1);
    if (!Number.isFinite(n) || n < 1) {
      toast.error(T("vPeople", "Nombre de personnes invalide."));
      return false;
    }

    if (!rNom.trim()) {
      toast.error(T("vFullName", "Veuillez saisir votre nom complet."));
      return false;
    }
    if (!rEmail.trim()) {
      toast.error(T("vEmail", "Veuillez saisir votre email."));
      return false;
    }
    if (!rTel.trim()) {
      toast.error(T("vPhone", "Veuillez saisir votre téléphone."));
      return false;
    }
    if (!rAddr.trim()) {
      toast.error(T("vAddress", "Veuillez saisir votre adresse."));
      return false;
    }

    if (!rAccept) {
      toast.error(T("vAcceptCgv", "Veuillez accepter les conditions de paiement (CMI/CGV)."));
      return false;
    }

    return true;
  };

  const handleReserveSubmit = async () => {
    if (!validateReserve()) return;

    const n = Number(rPeople || 1);

    // ⚠️ Hypothèse: tour.prix est "par personne". Si c'est "total", remplace par tour.prix.
    const unitPrice = Number(activeTour?.prix || 0);
    const total = Math.max(0, unitPrice * n);
    console.log("id de la visite : ",activeTour?._id)
    const payload = {
      visite: String(activeTour?._id || "").trim(), // ✅ IMPORTANT
      location: String(activeTour?.ville || "").trim(),
      date: rDate,

      tourType: mapTourTypeToFront(activeTour?.type) || null,
      numberOfPeople: n,

      dureeHeures: {
        min: Number(activeTour?.duree || 0) || null,
        max: Number(activeTour?.duree || 0) || null,
      },

      budgetTotal: { min: total, max: total },

      souhaiteReduction: false,

      comments: (rComments || "").trim()
        ? (rComments || "").trim()
        : `Réservation depuis une visite existante: ${String(activeTour?.titre || "").trim()}`,

      client: {
        nomComplet: rNom.trim(),
        email: rEmail.trim(),
        telephone: rTel.trim(),
        adresse: rAddr.trim(),
      },

      acceptCgv: rAccept,
      lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
    };



    try {
      setRSending(true);

      // ====== MODE "DIRECT" : paiement CMI immédiat ======
      if (paymentMode === "direct") {
        if (!(total > 0)) {
          toast.error(T("invalidAmount", "Prix indisponible pour cette visite."));
          setRSending(false);
          return;
        }
        const cmiPayload = {
          visiteId: String(activeTour?._id || "").trim(),
          date: rDate,
          nbPersonnes: n,
          utilisateur: rNom.trim() || rEmail.trim(),
          email: rEmail.trim(),
          fullName: rNom.trim(),
          phone: rTel.trim(),
          address: rAddr.trim(),
          prixTotal: total,
          montant: total,
          lang: (language || "fr").toString().slice(0, 2).toLowerCase(),
        };
        toast.loading(T("redirectingToPayment", "Redirection vers le paiement sécurisé..."), { id: "cmi-visite" });
        const html = await cmiService.createVisitePayment(cmiPayload);
        toast.success(T("redirectingToPayment", "Redirection vers le paiement sécurisé..."), { id: "cmi-visite", duration: 1500 });
        setReserveOpen(false);
        setTimeout(() => {
          document.open();
          document.write(html);
          document.close();
        }, 500);
        return;
      }

      // ====== MODE "EMAIL" : demande, lien envoyé par l'admin ======
      await submitDemande(payload);
      toast.success(T("reserveSent", "Réservation envoyée ✅. Un agent vous contactera avec le lien CMI."));
      setReserveOpen(false);
    } catch (e) {
      console.error(e);
      notifyApiError(e, T("sendError", "Erreur lors de l’envoi. Veuillez réessayer."), { id: "cmi-visite" });
    } finally {
      setRSending(false);
    }
  };

  // ===== Wizard (formulaire de demande) =====
  const FORM_STEPS = [
    { label: T("wizVisit", "Visite"), icon: MapPin },
    { label: T("wizInfos", "Vos infos"), icon: User },
    { label: T("wizPayment", "Paiement"), icon: Wallet },
  ];
  const lastFormStep = FORM_STEPS.length - 1;

  const validateFormStep = (s) => {
    if (s === 0) {
      if (!selectedCity) return T("vChooseCity", "Veuillez choisir la ville / lieu.");
      if (selectedCity === "Autre" && !customLocation.trim())
        return T("vSpecifyPlace", "Veuillez préciser le lieu.");
      if (!date) return T("vChooseDate", "Veuillez choisir la date.");
    }
    if (s === 1) {
      if (!nomComplet.trim()) return T("vFullName", "Veuillez saisir votre nom complet.");
      if (!email.trim()) return T("vEmail", "Veuillez saisir votre email.");
      if (!telephone.trim()) return T("vPhone", "Veuillez saisir votre téléphone.");
      if (!adresse.trim()) return T("vAddress", "Veuillez saisir votre adresse.");
    }
    return null;
  };
  const formNext = () => {
    const err = validateFormStep(formStep);
    if (err) { toast.error(err); return; }
    setFormStep((s) => Math.min(lastFormStep, s + 1));
  };
  const formBack = () => setFormStep((s) => Math.max(0, s - 1));

  return (
    <div
      className="nouzha-premium-skin py-24 min-h-screen relative overflow-hidden"
      style={{ background: "linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)" }}
    >
      {/* Pattern zellij */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${zelijPattern})`,
          backgroundRepeat: "repeat",
          backgroundSize: "400px",
          backgroundAttachment: "fixed",
          opacity: 0.4,
        }}
      />
      <div className="absolute inset-0 bg-white/80 z-0 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-orange-700 mb-4 drop-shadow-lg">
            {T("guidedToursTitle", "Visites guidées")}
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            {T(
              "guidedToursSubtitleNew",
              "Choisissez une visite existante ou envoyez une demande sur mesure. Notre équipe fixera le prix final et vous enverra un lien de paiement sécurisé CMI."
            )}
          </p>
        </motion.div>

        {/* ✅ Mode buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
          <button
            type="button"
            onClick={() => setMode("form")}
            className={`px-7 py-3 rounded-2xl text-sm font-black uppercase tracking-[0.1em] transition-all shadow-sm ${
              mode === "form"
                ? "bg-[#0f172a] text-white shadow-xl"
                : "bg-white/70 text-gray-500 hover:bg-white hover:text-orange-600"
            }`}
          >
            {T("modeForm", "📝 Remplir le formulaire")}
          </button>

          <button
            type="button"
            onClick={() => setMode("existing")}
            className={`px-7 py-3 rounded-2xl text-sm font-black uppercase tracking-[0.1em] transition-all shadow-sm ${
              mode === "existing"
                ? "bg-[#0f172a] text-white shadow-xl"
                : "bg-white/70 text-gray-500 hover:bg-white hover:text-orange-600"
            }`}
          >
            {T("modeExisting", "🧭 Choisir une visite existante")}
          </button>
        </div>

        {/* =============================
            MODE: EXISTING TOURS
        ============================== */}
        {mode === "existing" ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
            {/* Search/filters */}
            <div className="nouzha-catalog-card bg-white/90 rounded-3xl shadow-xl border border-orange-100 p-4 md:p-6 mb-6">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 border-2 border-orange-200 rounded-2xl px-4 py-3 bg-white/70">
                    <Search className="w-5 h-5 text-orange-500" />
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder={T("searchTours", "Rechercher: titre, ville, description...")}
                      className="w-full bg-transparent outline-none"
                    />
                  </div>
                </div>

                <div className="min-w-[210px]">
                  <div className="flex items-center gap-2 border-2 border-orange-200 rounded-2xl px-4 py-3 bg-white/70">
                    <Filter className="w-5 h-5 text-orange-500" />
                    <select
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                      className="w-full bg-transparent outline-none"
                    >
                      <option value="">{T("allCities", "Toutes les villes")}</option>
                      {[...new Set((tours || []).map((t) => t?.ville).filter(Boolean))].sort().map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="min-w-[210px]">
                  <div className="flex items-center gap-2 border-2 border-orange-200 rounded-2xl px-4 py-3 bg-white/70">
                    <Tag className="w-5 h-5 text-orange-500" />
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="w-full bg-transparent outline-none"
                    >
                      <option value="">{T("allTypes", "Tous les types")}</option>
                      {["culturel", "historique", "nature", "gastronomique", "autre"].map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={fetchTours}
                  className="px-4 py-3 rounded-2xl bg-white border border-orange-200 font-semibold hover:bg-orange-50 transition"
                >
                  {T("refresh", "Rafraîchir")}
                </button>
              </div>
            </div>

            {/* Grid */}
            <div className="nouzha-catalog-card bg-white/90 rounded-3xl shadow-xl border border-orange-100 p-4 md:p-6">
              {toursLoading ? (
                <div className="py-10 text-center text-gray-700">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-5 h-5 border-t-2 border-b-2 border-gray-700 rounded-full animate-spin" />
                    {T("loading", "Chargement...")}
                  </div>
                </div>
              ) : toursError ? (
                <div className="py-10 text-center text-red-600">{toursError}</div>
              ) : filteredTours.length === 0 ? (
                <div className="py-10 text-center text-gray-700">
                  {T("noTours", "Aucune visite trouvée.")}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredTours.map((tour) => {
                    const img = tour?.image ? (tour.image.startsWith("http") ? tour.image : `${API_BASE}${tour.image}`) : "";
                    const rating = Number(tour?.note_moyenne || 0);
                    const reviews = Number(tour?.nombre_avis || 0);

                    return (
                      <motion.div
                        key={tour?._id}
                        whileHover={{ y: -3 }}
                        className="rounded-3xl overflow-hidden border border-orange-100 bg-white shadow-sm hover:shadow-md transition"
                      >
                        <div className="relative h-44 bg-orange-50">
                          {img ? (
                            <img src={img} alt={tour?.titre || "tour"} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-orange-700 font-bold">
                              {T("noImage", "Visite guidée")}
                            </div>
                          )}
                          <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/90 border border-orange-100 text-xs font-bold text-orange-700">
                            {String(tour?.type || "").toUpperCase() || "TOUR"}
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="font-extrabold text-gray-900 text-lg line-clamp-2">{tour?.titre}</div>

                          <div className="mt-2 flex flex-col gap-1 text-sm text-gray-700">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-orange-500" />
                              <span className="font-semibold">{tour?.ville}</span>
                            </div>

                            <div className="flex items-center gap-2">
                              <CalendarIcon className="w-4 h-4 text-orange-500" />
                              <span>{formatDate(tour?.date, language)}</span>
                            </div>

                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-orange-500" />
                              <span>
                                {Number(tour?.duree || 0)} {T("hours", "heures")}
                              </span>
                            </div>
                          </div>

                          <div className="mt-3 flex items-center justify-between gap-2">
                            <div className="font-extrabold text-orange-700 text-lg">
                              {Number(tour?.prix || 0)} MAD
                              <span className="text-xs text-gray-500 font-semibold"> {T("perPerson", "/pers")}</span>
                            </div>

                            <div className="flex items-center gap-1 text-sm text-gray-700">
                              <Star className="w-4 h-4 text-orange-500" />
                              <span className="font-bold">{rating.toFixed(1)}</span>
                              <span className="text-gray-500">({reviews})</span>
                            </div>
                          </div>

                          <div className="mt-4 flex gap-2">
                            <button
                              type="button"
                              onClick={() => openDetails(tour)}
                              className="flex-1 px-4 py-3 rounded-2xl bg-white border border-orange-200 font-bold text-gray-900 hover:bg-orange-50 transition inline-flex items-center justify-center gap-2"
                            >
                              <Eye className="w-5 h-5 text-orange-600" />
                              {T("viewDetails", "Détails")}
                            </button>

                            <button
                              type="button"
                              onClick={() => openReserve(tour)}
                              className="flex-1 px-4 py-3 rounded-2xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-extrabold hover:from-orange-600 hover:to-red-700 transition inline-flex items-center justify-center gap-2"
                            >
                              <CheckCircle2 className="w-5 h-5" />
                              {T("reserve", "Réserver")}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* DETAILS MODAL */}
            <ModalShell
              open={detailsOpen}
              onClose={() => setDetailsOpen(false)}
              title={activeTour?.titre || T("details", "Détails de la visite")}
            >
              {activeTour ? (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
                      <div className="text-sm text-gray-500">{T("cityPlace", "Ville / lieu")}</div>
                      <div className="font-bold text-gray-900 flex items-center gap-2 mt-1">
                        <MapPin className="w-4 h-4 text-orange-600" /> {activeTour?.ville}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
                      <div className="text-sm text-gray-500">{T("dateLabel", "Date")}</div>
                      <div className="font-bold text-gray-900 flex items-center gap-2 mt-1">
                        <CalendarIcon className="w-4 h-4 text-orange-600" /> {formatDate(activeTour?.date, language)}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
                      <div className="text-sm text-gray-500">{T("durationHours", "Durée")}</div>
                      <div className="font-bold text-gray-900 flex items-center gap-2 mt-1">
                        <Clock className="w-4 h-4 text-orange-600" /> {Number(activeTour?.duree || 0)}{" "}
                        {T("hours", "heures")}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
                      <div className="text-sm text-gray-500">{T("price", "Prix")}</div>
                      <div className="font-extrabold text-orange-700 text-lg mt-1">
                        {Number(activeTour?.prix || 0)} MAD{" "}
                        <span className="text-xs text-gray-500 font-semibold">{T("perPerson", "/pers")}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="font-extrabold text-gray-900 mb-2">{T("description", "Description")}</div>
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {activeTour?.description || T("noDesc", "Aucune description.")}
                    </div>
                  </div>

                  {normalizeList(activeTour?.points_forts).length > 0 && (
                    <div>
                      <div className="font-extrabold text-gray-900 mb-2">{T("highlights", "Points forts")}</div>
                      <ul className="list-disc pl-6 text-gray-700 space-y-1">
                        {normalizeList(activeTour?.points_forts).map((x, idx) => (
                          <li key={idx}>{x}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {normalizeList(activeTour?.inclusions).length > 0 && (
                    <div>
                      <div className="font-extrabold text-gray-900 mb-2">{T("included", "Inclusions")}</div>
                      <ul className="list-disc pl-6 text-gray-700 space-y-1">
                        {normalizeList(activeTour?.inclusions).map((x, idx) => (
                          <li key={idx}>{x}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {normalizeList(activeTour?.exclusions).length > 0 && (
                    <div>
                      <div className="font-extrabold text-gray-900 mb-2">{T("excluded", "Exclusions")}</div>
                      <ul className="list-disc pl-6 text-gray-700 space-y-1">
                        {normalizeList(activeTour?.exclusions).map((x, idx) => (
                          <li key={idx}>{x}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setDetailsOpen(false);
                        openReserve(activeTour);
                      }}
                      className="w-full px-5 py-4 rounded-2xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-extrabold hover:from-orange-600 hover:to-red-700 transition inline-flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      {T("reserveThis", "Réserver cette visite")}
                    </button>
                  </div>
                </div>
              ) : null}
            </ModalShell>

            {/* RESERVE MODAL */}
            <ModalShell
              open={reserveOpen}
              onClose={() => setReserveOpen(false)}
              title={activeTour ? `${T("reserve", "Réserver")} — ${activeTour?.titre}` : T("reserve", "Réserver")}
            >
              {activeTour ? (
                <div className="space-y-6">
                  {/* recap */}
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-gray-800">
                        <MapPin className="w-4 h-4 text-orange-600" />
                        <span className="font-bold">{activeTour?.ville}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-800">
                        <Clock className="w-4 h-4 text-orange-600" />
                        <span className="font-bold">
                          {Number(activeTour?.duree || 0)} {T("hours", "heures")}
                        </span>
                      </div>
                      <div className="text-orange-700 font-extrabold">
                        {Number(activeTour?.prix || 0)} MAD <span className="text-xs text-gray-500 font-semibold">{T("perPerson", "/pers")}</span>
                      </div>
                    </div>
                  </div>

                  {/* reservation fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block font-semibold mb-2 text-gray-800">{T("dateLabel", "Date")} *</label>
                      <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                        <CalendarIcon className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="date"
                          value={rDate}
                          onChange={(e) => setRDate(e.target.value)}
                          min={today}
                          className="w-full outline-none bg-transparent"
                          required
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {T("dateHintTour", "Par défaut la date de la visite est pré-remplie.")}
                      </p>
                    </div>

                    <div>
                      <label className="block font-semibold mb-2 text-gray-800">{T("peopleCount", "Nombre de personnes")} *</label>
                      <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                        <Users className="mr-2 h-5 w-5 text-orange-500" />
                        <select
                          value={rPeople}
                          onChange={(e) => setRPeople(Number(e.target.value))}
                          className="w-full outline-none bg-transparent"
                        >
                          {[...Array(10)].map((_, i) => (
                            <option key={i} value={i + 1}>
                              {language === "en"
                                ? `${i + 1} ${i === 0 ? "person" : "people"}`
                                : `${i + 1} personne${i > 0 ? "s" : ""}`}
                            </option>
                          ))}
                          <option value={11}>{language === "en" ? "11+ people" : "11+ personnes"}</option>
                        </select>
                      </div>

                      <div className="mt-2 text-sm text-gray-700">
                        <span className="font-semibold">{T("estimatedTotal", "Total estimé")}:</span>{" "}
                        <span className="font-extrabold text-orange-700">
                          {Math.max(0, Number(activeTour?.prix || 0) * Number(rPeople || 1))} MAD
                        </span>
                        <span className="text-xs text-gray-500"> ({T("finalPriceByAgent", "prix final confirmé par un agent")})</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block font-semibold mb-2 text-gray-800">{T("commentsLabel", "Commentaires / besoins spécifiques")}</label>
                    <div className="flex items-start border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <MessageSquare className="mr-2 h-5 w-5 text-orange-500 mt-1" />
                      <textarea
                        value={rComments}
                        onChange={(e) => setRComments(e.target.value)}
                        placeholder={T("guidedCommentsPh", "Ex: guide francophone, visite privée, points d’intérêt...")}
                        className="w-full outline-none bg-transparent min-h-[110px]"
                      />
                    </div>
                  </div>

                  {/* Personal info */}
                  <div className="pt-2">
                    <h3 className="text-lg font-extrabold text-gray-900 mb-3">{T("personalInfo", "Informations personnelles")}</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block font-semibold mb-2 text-gray-800">{T("fullName", "Nom complet")} *</label>
                        <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                          <User className="mr-2 h-5 w-5 text-orange-500" />
                          <input
                            type="text"
                            value={rNom}
                            onChange={(e) => setRNom(e.target.value)}
                            className="w-full outline-none bg-transparent"
                            placeholder={T("fullNamePh", "Ex: Nom complet")}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block font-semibold mb-2 text-gray-800">
                          {currentUser ? T("emailAccount", "Email (compte)") : `${T("email", "Email")} *`}
                        </label>

                        <div
                          className={`flex items-center border-2 rounded-xl px-4 py-3 ${
                            currentUser
                              ? "bg-gray-100 border-gray-200"
                              : "bg-white/70 border-orange-200 focus-within:ring-2 focus-within:ring-orange-400 focus-within:border-orange-400"
                          }`}
                        >
                          <Mail className="mr-2 h-5 w-5 text-orange-500" />
                          <input
                            type="email"
                            value={rEmail}
                            onChange={(e) => setREmail(e.target.value)}
                            className={`w-full outline-none bg-transparent ${currentUser ? "cursor-not-allowed text-gray-600" : ""}`}
                            placeholder={T("emailPh", "vous@example.com")}
                            readOnly={!!currentUser}
                            disabled={!!currentUser}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block font-semibold mb-2 text-gray-800">{T("phone", "Téléphone")} *</label>
                        <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                          <Phone className="mr-2 h-5 w-5 text-orange-500" />
                          <input
                            type="tel"
                            value={rTel}
                            onChange={(e) => setRTel(e.target.value)}
                            className="w-full outline-none bg-transparent"
                            placeholder={T("phonePh", "Ex: +212 6xx xx xx xx")}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block font-semibold mb-2 text-gray-800">{T("address", "Adresse")} *</label>
                        <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                          <Home className="mr-2 h-5 w-5 text-orange-500" />
                          <input
                            type="text"
                            value={rAddr}
                            onChange={(e) => setRAddr(e.target.value)}
                            className="w-full outline-none bg-transparent"
                            placeholder={T("addressPh", "Ex: Quartier, rue, ville...")}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* CGV */}
                  <div className="p-4 bg-white border border-orange-200 rounded-2xl">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rAccept}
                        onChange={(e) => setRAccept(e.target.checked)}
                        className="mt-1 h-4 w-4 accent-orange-600"
                      />
                      <span className="text-sm text-gray-800">
                        {T("cgvTextPrefix", "J’ai lu et j’accepte les")}{" "}
                        <a href="/cgv" target="_blank" rel="noreferrer" className="text-orange-600 font-semibold hover:underline">
                          {T("cgvLinkText", "Conditions Générales de Vente")}
                        </a>{" "}
                        {T("cgvTextSuffix", "ainsi que les conditions de paiement via CMI.")}
                      </span>
                    </label>

                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-gray-500">{T("securePaymentLabel", "Paiement sécurisé :")}</span>
                      <a
                        href="/paiement-securise"
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-orange-600 font-semibold hover:underline"
                      >
                        {T("securePaymentLink", "Voir les informations CMI / Visa / MasterCard / 3DS")}
                      </a>
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="button"
                    onClick={handleReserveSubmit}
                    disabled={rSending || !rAccept}
                    className={`w-full px-6 py-4 rounded-2xl font-extrabold shadow-lg inline-flex items-center justify-center gap-3 transition ${
                      rSending || !rAccept
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700"
                    }`}
                  >
                    {rSending ? (
                      <>
                        <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin" />
                        {T("sending", "Envoi en cours...")}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {T("submitReservation", "Envoyer ma réservation")}
                      </>
                    )}
                  </button>

                  <div className="mt-2 flex items-center justify-center gap-2 text-xs text-gray-500">
                    <Wallet className="w-4 h-4" />
                    {T("cmiFooter", "Paiement final via lien sécurisé CMI (3DS)")}
                  </div>
                </div>
              ) : null}
            </ModalShell>
          </motion.div>
        ) : null}

        {/* =============================
            MODE: FORM (DEMANDE)
        ============================== */}
        {mode === "form" ? (
          <>
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white/90 rounded-3xl shadow-xl p-8 text-center max-w-2xl mx-auto"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send className="w-10 h-10 text-green-600" />
                </div>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {T("successTitle", "Demande envoyée avec succès !")}
                </h2>
                <p className="text-gray-600 mb-6">
                  {T(
                    "successGuidedBody",
                    "Nous avons bien reçu votre demande de visite guidée. Un agent va l’étudier et vous contactera par email avec une proposition et un lien de paiement sécurisé."
                  )}
                </p>

                <button
                  onClick={() => setSuccess(false)}
                  className="px-6 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all font-semibold"
                >
                  {T("successNewRequest", "Faire une autre demande")}
                </button>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.45, delay: 0.1 }}
                onSubmit={handleSubmitForm}
                className="rounded-[2rem] p-0"
              >
                {/* ===== Stepper ===== */}
                <div className="flex items-center justify-between gap-2 mb-8 px-2">
                  {FORM_STEPS.map((st, i) => {
                    const Icon = st.icon;
                    const done = i < formStep;
                    const active = i === formStep;
                    return (
                      <React.Fragment key={st.label}>
                        <div className="flex flex-col items-center text-center">
                          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm transition-all duration-300 ${active ? "bg-orange-600 text-white scale-110" : done ? "bg-green-500 text-white" : "bg-white text-gray-400 border border-gray-200"}`}>
                            {done ? <CheckCircle2 className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                          </div>
                          <span className={`mt-2 text-[11px] md:text-xs font-bold uppercase tracking-wide ${active ? "text-orange-700" : "text-gray-400"}`}>{st.label}</span>
                        </div>
                        {i < FORM_STEPS.length - 1 && (
                          <div className={`h-1 flex-1 rounded-full mb-6 transition-all duration-300 ${i < formStep ? "bg-green-500" : "bg-gray-200"}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>

                {formStep === 0 && (
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <Tag className="w-4 h-4" />
                    </div>
                    <h2 className="text-2xl md:text-[1.9rem] leading-tight font-black uppercase tracking-tight text-[#111827]">
                      {T("guidedInfo", "Informations de la visite guidée")}
                    </h2>
                  </div>

                  {/* Dictée vocale + Autofill (IA) */}
                  <VoiceDictation
                    stt={stt}
                    onAutofill={handleGeminiAutofill}
                    placeholder={T("transcriptPlaceholder", "La transcription s’affiche ici...")}
                    hint={T("guidedVoiceHint", "Astuce : dites la ville, la date, le nombre de personnes et le type de visite souhaité.")}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Ville / lieu */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">
                      {T("cityPlace", "Ville / lieu")} *
                    </label>

                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <MapPin className="mr-2 h-5 w-5 text-orange-500" />
                      <select
                        value={selectedCity}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSelectedCity(v);
                          if (v !== "Autre") setCustomLocation("");
                        }}
                        className="w-full outline-none bg-transparent"
                        required
                      >
                        <option value="" disabled>
                          {T("chooseCity", "Choisir une ville...")}
                        </option>
                        {moroccanCities.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedCity === "Autre" && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                          {T("specifyPlace", "Précisez le lieu")} *
                        </label>
                        <input
                          type="text"
                          value={customLocation}
                          onChange={(e) => setCustomLocation(e.target.value)}
                          placeholder={T("specifyPlacePh", "Ex: Volubilis, Vallée du Ziz...")}
                          className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                          required
                        />
                      </div>
                    )}
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("dateLabel", "Date")} *</label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <CalendarIcon className="mr-2 h-5 w-5 text-orange-500" />
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        min={today}
                        className="w-full outline-none bg-transparent"
                        required
                      />
                    </div>
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("tourType", "Type de visite")}</label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <Tag className="mr-2 h-5 w-5 text-orange-500" />
                      <select
                        value={tourType}
                        onChange={(e) => setTourType(e.target.value)}
                        className="w-full outline-none bg-transparent"
                      >
                        <option value="">{T("any", "Peu importe")}</option>
                        <option value="cultural">{T("tourCultural", "Culturelle")}</option>
                        <option value="historical">{T("tourHistorical", "Historique")}</option>
                        <option value="nature">{T("tourNature", "Nature")}</option>
                        <option value="adventure">{T("tourAdventure", "Aventure")}</option>
                      </select>
                    </div>
                  </div>

                  {/* Nb personnes */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">
                      {T("peopleCount", "Nombre de personnes")} *
                    </label>
                    <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <Users className="mr-2 h-5 w-5 text-orange-500" />
                      <select
                        value={numberOfPeople}
                        onChange={(e) => setNumberOfPeople(e.target.value)}
                        className="w-full outline-none bg-transparent"
                        required
                      >
                        {[...Array(10)].map((_, i) => (
                          <option key={i} value={i + 1}>
                            {language === "en"
                              ? `${i + 1} ${i === 0 ? "person" : "people"}`
                              : `${i + 1} personne${i > 0 ? "s" : ""}`}
                          </option>
                        ))}
                        <option value="11">{language === "en" ? "11+ people" : "11+ personnes"}</option>
                      </select>
                    </div>
                  </div>

                  {/* Durée */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">
                      {T("durationHours", "Durée souhaitée (heures)")} *
                    </label>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-orange-500" />
                        <span className="text-sm text-gray-600 w-16">{T("minLabel", "Min")}</span>
                        <input
                          type="number"
                          min="1"
                          value={dureeMin}
                          onChange={(e) => setDureeMin(Number(e.target.value))}
                          className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-orange-500 opacity-0" />
                        <span className="text-sm text-gray-600 w-16">{T("maxLabel", "Max")}</span>
                        <input
                          type="number"
                          min="1"
                          value={dureeMax}
                          onChange={(e) => setDureeMax(Number(e.target.value))}
                          className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{T("durationHint", "Ex: 2h–4h pour médina, 6h–8h pour excursion.")}</p>
                  </div>

                  {/* Budget total */}
                  <div>
                    <label className="block font-medium mb-2 text-gray-700">{T("budgetTotal", "Budget total (MAD)")} *</label>
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-16">{T("minLabel", "Min")}</span>
                        <input
                          type="number"
                          min="0"
                          value={prixMin}
                          onChange={(e) => setPrixMin(Number(e.target.value))}
                          className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-16">{T("maxLabel", "Max")}</span>
                        <input
                          type="number"
                          min="0"
                          value={prixMax}
                          onChange={(e) => setPrixMax(Number(e.target.value))}
                          className="w-full border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70 focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{T("budgetHint", "Indique un intervalle réaliste pour accélérer le traitement.")}</p>
                  </div>
                  </div>
                </section>
                )}

                {formStep === 1 && (
                <>
                {/* Préférences */}
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <BadgePercent className="w-4 h-4" />
                    </div>
                    <h3 className="text-2xl md:text-[1.65rem] leading-tight font-black uppercase tracking-tight text-[#111827]">{T("guidedPreferences", "Détails / préférences")}</h3>
                  </div>

                  <div className="mt-4 flex items-center gap-3 p-4 bg-white border border-orange-200 rounded-2xl">
                    <input
                      type="checkbox"
                      checked={souhaiteReduction}
                      onChange={(e) => setSouhaiteReduction(e.target.checked)}
                      className="h-4 w-4 accent-orange-600"
                    />
                    <BadgePercent className="w-5 h-5 text-orange-600" />
                    <span className="text-sm text-gray-800">
                      {T("discountWish", "Je souhaite recevoir une offre avec réduction si disponible")}
                    </span>
                  </div>

                  <div className="mt-4">
                    <label className="block font-medium mb-2 text-gray-700">{T("commentsLabel", "Commentaires / besoins spécifiques")}</label>
                    <div className="flex items-start border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                      <MessageSquare className="mr-2 h-5 w-5 text-orange-500 mt-1" />
                      <textarea
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder={T("guidedCommentsPh", "Ex: guide francophone, accessible, visite privée, points d’intérêt...")}
                        className="w-full outline-none bg-transparent min-h-[110px]"
                      />
                    </div>
                  </div>
                </section>

                {/* Infos personnelles */}
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <User className="w-4 h-4" />
                    </div>
                    <h2 className="text-2xl md:text-[1.65rem] leading-tight font-black uppercase tracking-tight text-[#111827]">
                      {T("personalInfo", "Informations personnelles")}
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium mb-2 text-gray-700">{T("fullName", "Nom complet")} *</label>
                      <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                        <User className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="text"
                          value={nomComplet}
                          onChange={(e) => setNomComplet(e.target.value)}
                          className="w-full outline-none bg-transparent"
                          placeholder={T("fullNamePh", "Ex: Nom complet")}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium mb-2 text-gray-700">
                        {currentUser ? T("emailAccount", "Email (compte)") : `${T("email", "Email")} *`}
                      </label>

                      <div
                        className={`flex items-center border-2 rounded-xl px-4 py-3 ${
                          currentUser
                            ? "bg-gray-100 border-gray-200"
                            : "bg-white/70 border-orange-200 focus-within:ring-2 focus-within:ring-orange-400 focus-within:border-orange-400"
                        }`}
                      >
                        <Mail className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className={`w-full outline-none bg-transparent ${currentUser ? "cursor-not-allowed text-gray-600" : ""}`}
                          placeholder={T("emailPh", "vous@example.com")}
                          readOnly={!!currentUser}
                          disabled={!!currentUser}
                          required
                        />
                      </div>

                      {currentUser && (
                        <p className="mt-1 text-sm text-gray-500">
                          {T("emailLockedHint", "Cette adresse est liée à votre compte et ne peut pas être modifiée ici.")}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block font-medium mb-2 text-gray-700">{T("phone", "Téléphone")} *</label>
                      <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                        <Phone className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="tel"
                          value={telephone}
                          onChange={(e) => setTelephone(e.target.value)}
                          className="w-full outline-none bg-transparent"
                          placeholder={T("phonePh", "Ex: +212 6xx xx xx xx")}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium mb-2 text-gray-700">{T("address", "Adresse")} *</label>
                      <div className="flex items-center border-2 border-orange-200 rounded-xl px-4 py-3 bg-white/70">
                        <Home className="mr-2 h-5 w-5 text-orange-500" />
                        <input
                          type="text"
                          value={adresse}
                          onChange={(e) => setAdresse(e.target.value)}
                          className="w-full outline-none bg-transparent"
                          placeholder={T("addressPh", "Ex: Quartier, rue, ville...")}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </section>
                </>
                )}

                {formStep === 2 && (
                <section className="mb-8 p-6 rounded-[1.75rem] border border-orange-100 bg-white/80 shadow-[0_8px_22px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow">
                      <Wallet className="w-4 h-4" />
                    </div>
                    <h3 className="text-2xl md:text-[1.65rem] leading-tight font-black uppercase tracking-tight text-[#111827]">{T("paymentConditions", "Conditions & paiement")}</h3>
                  </div>

                  <div className="p-4 bg-white border border-orange-200 rounded-2xl">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acceptCgv}
                      onChange={(e) => setAcceptCgv(e.target.checked)}
                      className="mt-1 h-4 w-4 accent-orange-600"
                    />
                    <span className="text-sm text-gray-800">
                      {T("cgvTextPrefix", "J’ai lu et j’accepte les")}{" "}
                      <a href="/cgv" target="_blank" rel="noreferrer" className="text-orange-600 font-semibold hover:underline">
                        {T("cgvLinkText", "Conditions Générales de Vente")}
                      </a>{" "}
                      {T("cgvTextSuffix", "ainsi que les conditions de paiement via CMI.")}
                    </span>
                  </label>

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500">{T("securePaymentLabel", "Paiement sécurisé :")}</span>
                    <a
                      href="/paiement-securise"
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-orange-600 font-semibold hover:underline"
                    >
                      {T("securePaymentLink", "Voir les informations CMI / Visa / MasterCard / 3DS")}
                    </a>
                  </div>
                  </div>
                </section>
                )}

                {/* ===== Navigation wizard ===== */}
                <div className="flex items-center justify-between gap-4">
                  {formStep > 0 ? (
                    <button
                      type="button"
                      onClick={formBack}
                      className="inline-flex items-center gap-2 px-7 py-4 rounded-xl font-black uppercase tracking-widest text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm transition-all"
                    >
                      ← {T("wizBack", "Précédent")}
                    </button>
                  ) : (
                    <span />
                  )}

                  {formStep < lastFormStep ? (
                    <button
                      type="button"
                      onClick={formNext}
                      className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-black uppercase tracking-widest text-sm shadow-lg hover:from-orange-600 hover:to-red-700 transition-all"
                    >
                      {T("wizNext", "Suivant")} →
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading || !acceptCgv}
                      className={`inline-flex items-center gap-3 px-10 py-4 rounded-xl font-black uppercase tracking-widest text-sm shadow-lg transition-all ${loading || !acceptCgv
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700"
                        }`}
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin" />
                          {T("sending", "Envoi en cours...")}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          {T("submitRequest", "Soumettre ma demande")}
                        </>
                      )}
                    </button>
                  )}
                </div>

                {formStep === lastFormStep && !acceptCgv && (
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    {T("mustAcceptCgv", "Pour envoyer la demande, vous devez d’abord accepter les conditions (CMI/CGV).")}
                  </p>
                )}

                <p className="text-xs text-gray-500 mt-3 max-w-2xl mx-auto text-center">
                  {T(
                    "afterSubmitGuidedHint",
                    "Après soumission, un agent traitera votre demande, sélectionnera la meilleure offre, fixera le tarif final et vous enverra un email avec un lien de paiement sécurisé."
                  )}
                </p>

                <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-500">
                  <Wallet className="w-4 h-4" />
                  {T("cmiFooter", "Paiement final via lien sécurisé CMI (3DS)")}
                </div>
              </motion.form>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default GuidedTours;
